import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { useAuth } from '../AuthProvider';
import { createBugReport, BugReportType, BugReportPriority } from '../../services/supabaseService';
import { CardImage } from '../CardImage';

export interface CardBugReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  card: any;
  cardCode?: string;
  gameName?: string;
  cardSet?: string;
  cardRarity?: string;
  activeImage?: string;
  selectedVariantInfo?: any;
}

const COMMON_ISSUES = [
  {
    label: 'Imagem incorreta ou não carrega',
    type: 'missing_data' as BugReportType,
    title: 'Imagem da carta incorreta ou ausente',
    description: 'A imagem exibida para esta carta está errada, não carrega ou pertence a outra versão.'
  },
  {
    label: 'Efeito / texto com erro',
    type: 'incorrect_data' as BugReportType,
    title: 'Texto ou efeito incorreto',
    description: 'O texto de regra ou habilidade desta carta possui erro de digitação, tradução ou divergência com a regra oficial.'
  },
  {
    label: 'Atributos errados (custo, poder, tipo)',
    type: 'incorrect_data' as BugReportType,
    title: 'Atributos divergentes da carta oficial',
    description: 'O custo, poder, defesa, vida ou tipo da carta está diferente da versão oficial.'
  },
  {
    label: 'Coleção ou raridade errada',
    type: 'incorrect_data' as BugReportType,
    title: 'Coleção ou raridade incorreta',
    description: 'O código de coleção ou a raridade vinculada a esta carta está incorreto.'
  },
  {
    label: 'Variante ou acabamento faltando',
    type: 'missing_data' as BugReportType,
    title: 'Variante ou acabamento não encontrado',
    description: 'Falta cadastrar uma variante importante desta carta (ex: Foil, Arte Alternativa, Promo).'
  }
];

export const CardBugReportModal: React.FC<CardBugReportModalProps> = ({
  isOpen,
  onClose,
  card,
  cardCode,
  gameName,
  cardSet,
  cardRarity,
  activeImage,
  selectedVariantInfo
}) => {
  const { user } = useAuth();

  const [reportType, setReportType] = useState<BugReportType>('incorrect_data');
  const [priority, setPriority] = useState<BugReportPriority>('normal');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submittedSuccess, setSubmittedSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  if (!isOpen || !card) return null;

  const handleSelectQuickIssue = (issue: typeof COMMON_ISSUES[0]) => {
    setReportType(issue.type);
    setTitle(issue.title);
    setDescription(issue.description);
    setErrorMessage(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!title.trim()) {
      setErrorMessage('Por favor, informe um título ou resumo do problema.');
      return;
    }

    if (!description.trim()) {
      setErrorMessage('Por favor, descreva o erro que você encontrou.');
      return;
    }

    setIsSubmitting(true);
    setErrorMessage(null);

    try {
      const cardIdentifier = String(card.id || cardCode || card.code || card.number || '');
      const metadata = {
        card_id: card.id,
        card_name: card.name,
        card_code: cardCode || card.code || card.number || null,
        game: card.game || gameName || null,
        set: cardSet || card.set?.name || null,
        rarity: cardRarity || selectedVariantInfo?.rarity || null,
        selected_variant: selectedVariantInfo?.setCode || null,
        image_url: activeImage || card.imageUrl || card.image_url || null,
        browser_url: typeof window !== 'undefined' ? window.location.href : null,
      };

      await createBugReport({
        userId: user?.id || null,
        reportType,
        contextType: 'card',
        contextId: cardIdentifier || null,
        title: title.trim(),
        description: description.trim(),
        pageUrl: typeof window !== 'undefined' ? window.location.href : null,
        metadata,
        priority
      });

      setSubmittedSuccess(true);
    } catch (err: any) {
      console.error('Falha ao enviar reporte de erro da carta:', err);
      setErrorMessage(
        err?.message || 'Não foi possível enviar o relatório no momento. Tente novamente em instantes.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (isSubmitting) return;
    setTitle('');
    setDescription('');
    setSubmittedSuccess(false);
    setErrorMessage(null);
    onClose();
  };

  return (
    <AnimatePresence>
      <div
        className="fixed inset-0 z-[120] flex items-center justify-center p-3 sm:p-4 bg-slate-950/85 backdrop-blur-md"
        onClick={handleClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0, y: 10 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.95, opacity: 0, y: 10 }}
          onClick={(e) => e.stopPropagation()}
          className="relative max-w-xl w-full bg-slate-900 border border-slate-700/80 rounded-2xl sm:rounded-3xl shadow-[0_20px_50px_rgba(0,0,0,0.7)] overflow-hidden flex flex-col max-h-[90vh]"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-slate-800 bg-slate-950/60">
            <div className="flex items-center space-x-2.5 min-w-0">
              <span className="w-8 h-8 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 flex items-center justify-center text-sm shrink-0">
                <i className="fas fa-flag"></i>
              </span>
              <div className="min-w-0">
                <h3 className="text-sm sm:text-base font-black text-white uppercase tracking-tight truncate">
                  Reportar Erro na Carta
                </h3>
                <p className="text-[11px] text-slate-400 truncate">
                  {card.name} {cardCode ? `(${cardCode})` : ''}
                </p>
              </div>
            </div>

            <button
              type="button"
              onClick={handleClose}
              className="w-8 h-8 rounded-full hover:bg-slate-800 text-slate-400 hover:text-white flex items-center justify-center transition-colors cursor-pointer"
            >
              <i className="fas fa-times text-sm"></i>
            </button>
          </div>

          {/* Body */}
          <div className="p-4 sm:p-6 overflow-y-auto space-y-4">
            {submittedSuccess ? (
              <div className="text-center py-6 sm:py-8 space-y-3">
                <div className="w-14 h-14 mx-auto rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 flex items-center justify-center text-2xl shadow-inner">
                  <i className="fas fa-check"></i>
                </div>
                <h4 className="text-base sm:text-lg font-black text-white">
                  Relatório Enviado com Sucesso!
                </h4>
                <p className="text-xs text-slate-300 max-w-md mx-auto leading-relaxed">
                  Obrigado por nos ajudar a manter a base de cartas atualizada e precisa.
                  Nossa equipe de dados irá analisar e aplicar a correção em breve.
                </p>
                <div className="pt-3">
                  <button
                    type="button"
                    onClick={handleClose}
                    className="px-6 py-2.5 bg-purple-600 hover:bg-purple-500 text-white font-bold text-xs rounded-xl shadow-lg transition-all cursor-pointer"
                  >
                    Concluir
                  </button>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Card Mini Context Card */}
                <div className="bg-slate-950/60 border border-slate-800 rounded-xl p-3 flex items-center space-x-3">
                  <div className="w-12 h-16 rounded-lg overflow-hidden bg-slate-900 border border-slate-800 shrink-0 flex items-center justify-center">
                    <CardImage
                      src={activeImage}
                      alt={card.name}
                      game={card.game || gameName}
                      className="w-full h-full object-contain"
                    />
                  </div>
                  <div className="min-w-0 flex-1 space-y-0.5">
                    <div className="text-xs font-bold text-white truncate">{card.name}</div>
                    <div className="text-[11px] text-slate-400 flex flex-wrap gap-x-2">
                      {cardCode && <span>ID: <strong className="text-slate-300">{cardCode}</strong></span>}
                      {cardSet && <span>Coleção: <strong className="text-slate-300">{cardSet}</strong></span>}
                      {(card.game || gameName) && (
                        <span>Jogo: <strong className="text-purple-400">{card.game || gameName}</strong></span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Quick selection chips */}
                <div className="space-y-1.5">
                  <label className="text-[11px] font-bold text-slate-400 uppercase tracking-wider block">
                    Problemas Comuns (clique para preencher)
                  </label>
                  <div className="flex flex-wrap gap-1.5">
                    {COMMON_ISSUES.map((issue, idx) => (
                      <button
                        key={`issue-chip-${idx}`}
                        type="button"
                        onClick={() => handleSelectQuickIssue(issue)}
                        className="text-[11px] px-2.5 py-1 rounded-lg bg-slate-800/80 hover:bg-purple-600/30 text-slate-300 hover:text-white border border-slate-700/60 hover:border-purple-500/40 transition-colors text-left cursor-pointer"
                      >
                        {issue.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Report Type Selector */}
                <div className="space-y-1.5">
                  <label className="text-[11px] font-bold text-slate-400 uppercase tracking-wider block">
                    Tipo do Relatório *
                  </label>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {[
                      { id: 'incorrect_data', label: 'Dados Incorretos', icon: 'fa-triangle-exclamation' },
                      { id: 'missing_data', label: 'Dados Faltando', icon: 'fa-circle-question' },
                      { id: 'bug', label: 'Erro Visual / Bug', icon: 'fa-bug' },
                      { id: 'suggestion', label: 'Sugestão', icon: 'fa-lightbulb' },
                      { id: 'other', label: 'Outro', icon: 'fa-ellipsis' }
                    ].map((opt) => (
                      <button
                        key={opt.id}
                        type="button"
                        onClick={() => setReportType(opt.id as BugReportType)}
                        className={`p-2 rounded-xl text-left border text-xs font-semibold flex items-center space-x-2 transition-all cursor-pointer ${
                          reportType === opt.id
                            ? 'bg-purple-600/20 border-purple-500 text-white'
                            : 'bg-slate-950/50 border-slate-800 text-slate-400 hover:text-slate-200 hover:border-slate-700'
                        }`}
                      >
                        <i className={`fas ${opt.icon} text-[11px] ${reportType === opt.id ? 'text-purple-400' : 'text-slate-500'}`}></i>
                        <span className="truncate">{opt.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Title Input */}
                <div className="space-y-1.5">
                  <label htmlFor="bug-title-input" className="text-[11px] font-bold text-slate-400 uppercase tracking-wider block">
                    Resumo do Problema *
                  </label>
                  <input
                    id="bug-title-input"
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="Ex: Imagem com erro, texto de efeito em inglês, etc."
                    className="w-full bg-slate-950/70 border border-slate-800 rounded-xl px-3 py-2 text-xs sm:text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-purple-500 transition-colors"
                    maxLength={140}
                  />
                </div>

                {/* Description Input */}
                <div className="space-y-1.5">
                  <label htmlFor="bug-desc-input" className="text-[11px] font-bold text-slate-400 uppercase tracking-wider block">
                    Descrição Detalhada *
                  </label>
                  <textarea
                    id="bug-desc-input"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Explique o que está errado e, se souber, qual é a informação correta..."
                    rows={3}
                    className="w-full bg-slate-950/70 border border-slate-800 rounded-xl p-3 text-xs sm:text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-purple-500 transition-colors resize-none"
                    maxLength={1000}
                  />
                </div>

                {/* Priority Selector (Compact) */}
                <div className="flex items-center justify-between pt-1">
                  <span className="text-[11px] font-bold text-slate-400">Gravidade:</span>
                  <div className="flex items-center space-x-1.5">
                    {(['low', 'normal', 'high'] as BugReportPriority[]).map((p) => {
                      const labels: Record<BugReportPriority, string> = {
                        low: 'Baixa',
                        normal: 'Normal',
                        high: 'Alta',
                        critical: 'Crítica'
                      };
                      return (
                        <button
                          key={p}
                          type="button"
                          onClick={() => setPriority(p)}
                          className={`px-2.5 py-1 rounded-lg text-[11px] font-bold capitalize transition-colors cursor-pointer ${
                            priority === p
                              ? 'bg-slate-800 text-white border border-purple-500/50'
                              : 'text-slate-400 hover:text-slate-300'
                          }`}
                        >
                          {labels[p]}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Error Banner */}
                {errorMessage && (
                  <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-xl text-xs text-red-400 flex items-center space-x-2">
                    <i className="fas fa-circle-exclamation shrink-0"></i>
                    <span>{errorMessage}</span>
                  </div>
                )}

                {/* Submit Buttons */}
                <div className="flex items-center justify-end space-x-2.5 pt-2 border-t border-slate-800">
                  <button
                    type="button"
                    onClick={handleClose}
                    disabled={isSubmitting}
                    className="px-4 py-2 rounded-xl text-xs font-bold text-slate-400 hover:text-white hover:bg-slate-800 transition-colors cursor-pointer"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="px-5 py-2 bg-red-600 hover:bg-red-500 disabled:opacity-50 text-white text-xs font-black uppercase tracking-wider rounded-xl shadow-lg flex items-center space-x-2 transition-all cursor-pointer"
                  >
                    {isSubmitting ? (
                      <>
                        <i className="fas fa-spinner fa-spin text-xs"></i>
                        <span>Enviando...</span>
                      </>
                    ) : (
                      <>
                        <i className="fas fa-paper-plane text-xs"></i>
                        <span>Enviar Relatório</span>
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};
